import React from "react";
import type { LoadPhase } from "../../../components/ui";

/**
 * A line's worth of space, showing nothing.
 *
 * This is how the blocks below know how tall to be. Give it the same `text-*`
 * classes the real line carries — and, for a control, the same padding and
 * border — and the non-breaking space inside makes it exactly that line's
 * height. It is `invisible` rather than drawn, so what comes out is one plain
 * block per card and not a diagram of the card's insides.
 *
 * A skeleton pinned to hand-measured heights cannot hold: the form changes, the
 * numbers do not, and the page goes back to stepping down as it opens. This
 * measures itself against the same classes the form is built from, so it
 * cannot drift from it.
 */
const Spacer: React.FC<{ className?: string }> = ({ className = "" }) => (
  <span className={`block invisible ${className}`}>&nbsp;</span>
);

/** A label over a control, at the geometry `TextField` gives them. */
const FieldSpace: React.FC = () => (
  <div>
    <Spacer className="mb-1.5 text-[13px] 3xl:text-[14px]" />
    <Spacer className="border border-transparent px-3.5 py-2.5 text-[14px] 3xl:py-3 3xl:text-[15px]" />
  </div>
);

/**
 * One of the form's two sections, as one block.
 *
 * Both are the same shape — a name and three short answers, then a street and
 * three more — so one component stands in for both.
 *
 * The caption's line is held open above the block rather than swallowed into
 * it. `FormSection` gives every section an `h-8` heading and an `mb-2` under
 * it, so the card it captions starts 40px down — which is exactly where the
 * summary card starts, and the two blocks have to begin on that same line or
 * the page reads as two columns that were laid out separately. Nothing is drawn
 * in it: a caption is three muted words, not a card, and shimmering it would
 * put a block above every block.
 *
 * The three fields in the row are all spaced out even though they are the same
 * height, because below `sm` they are not a row at all: `sm:grid-cols-3` is
 * what decides, and the block has to stack with them.
 */
const FormSectionBlock: React.FC = () => (
  <section aria-hidden="true">
    <div className="mb-2 h-8" />

    <div className="rounded-card bg-[#ECF0F3] skeleton-sheen p-4 sm:p-5 3xl:p-6">
      <FieldSpace />
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3 3xl:mt-5 3xl:gap-5">
        <FieldSpace />
        <FieldSpace />
        <FieldSpace />
      </div>
    </div>
  </section>
);

/**
 * The summary card, as one block.
 *
 * The spacers inside are `OrderSummary`'s own rows: the heading, the plan and
 * its list price, the offer that comes off it, the three lines that make up
 * the total, the total, the two milestones, and the button. Where a row sets
 * two sizes against each other the larger one is the whole of its height —
 * they are baseline-aligned, so the bigger line box is what the row comes to —
 * which is why only one spacer stands for each.
 */
const SummaryBlock: React.FC = () => (
  <div
    aria-hidden="true"
    className="rounded-card bg-[#ECF0F3] skeleton-sheen p-5 3xl:p-6"
  >
    <Spacer className="text-[16px] 3xl:text-[18px]" />

    <div className="mt-4">
      <Spacer className="text-[15px] 3xl:text-[16px]" />
      <Spacer className="mt-0.5 text-[12px] 3xl:text-[13px]" />
    </div>

    {/* The plan's own offer, taken off the list price above it. */}
    <Spacer className="mt-3 text-[13px] 3xl:text-[14px]" />

    {/* Subtotal, the coupon line, and the tax. */}
    <div className="mt-4 flex flex-col gap-2.5 pt-4">
      <Spacer className="text-[13px] 3xl:text-[14px]" />
      <Spacer className="text-[13px] 3xl:text-[14px]" />
      <Spacer className="text-[13px] 3xl:text-[14px]" />
    </div>

    <div className="mt-4 border-t border-transparent pt-4">
      <Spacer className="text-[21px] 3xl:text-[24px]" />
    </div>

    <div className="mt-5">
      {/* The stop that has happened: a title and one line under it. */}
      <div className="pb-4">
        <Spacer className="text-[13px] 3xl:text-[14px]" />
        <Spacer className="mt-0.5 text-[12px] 3xl:text-[13px]" />
      </div>
      {/* The one still to come, whose detail runs to two lines in this column. */}
      <div>
        <Spacer className="text-[13px] 3xl:text-[14px]" />
        <Spacer className="mt-0.5 text-[12px] 3xl:text-[13px]" />
        <Spacer className="text-[12px] 3xl:text-[13px]" />
      </div>
    </div>

    <Spacer className="mt-5 py-3.5 text-[15px]" />
  </div>
);

export interface CheckoutSkeletonProps {
  /**
   * How long the saved details have been coming. Only `slow` draws the page's
   * shape; while it is merely `waiting` the page is empty, so details that
   * arrive quickly are never preceded by a screenful of blocks.
   */
  phase: LoadPhase;
}

/**
 * The checkout while the details it fills itself from are still on their way.
 *
 * Three blocks, one per card, each with a single band of light crossing it —
 * the same way the dashboard waits. What a card turns out to hold is its own
 * business once it lands, and drawing every label and field twice is what makes
 * a loading screen look busier than the screen it stands for.
 *
 * Both columns, not just the form. The summary beside it depends on nothing
 * being fetched — the plan comes from the URL and its price is arithmetic — so
 * it *could* be drawn for real from the first frame, and used to be. But a
 * finished card of figures standing beside a shimmering form reads as a page
 * that arrived broken, and then rearranges itself when the rest lands. One
 * screen that fills in at one moment is the calmer thing, and the summary loses
 * nothing by waiting the few hundred milliseconds the form does.
 *
 * Returned as two grid children rather than as a wrapper, so the placeholders
 * sit in the same two columns the real thing does — the layout is the page's,
 * stated once, and this only fills it.
 */
const CheckoutSkeleton: React.FC<CheckoutSkeletonProps> = ({ phase }) => {
  if (phase !== "slow") {
    // Two empty cells rather than nothing: the grid keeps its shape, so the
    // page does not resize under the header on the way to being drawn.
    return (
      <>
        <div aria-hidden="true" />
        <div aria-hidden="true" />
      </>
    );
  }

  return (
    <>
      {/* The form's own column classes, so the two blocks are spaced the way
          the two sections that replace them are. */}
      <div className="flex flex-col gap-5 3xl:gap-6">
        <FormSectionBlock />
        <FormSectionBlock />
      </div>

      {/* Dropped the same 40px the real summary column is, so the card that
          replaces this one stands where it was shimmering rather than stepping
          down as the page fills in — and so this block starts on the line the
          form's first one does, that 40px being the form caption's on the other
          side. See the note on the page's own column. */}
      <div className="lg:mt-10">
        {/* The summary card, and only it. The marks under it are not drawn:
            they are a row of logos rather than a card, they are the last thing
            in the column so nothing moves when they land, and a block for them
            would be this screen shimmering something that was never waiting. */}
        <SummaryBlock />
      </div>
    </>
  );
};

export default CheckoutSkeleton;
